import { Pagination, PaginationContextProvider, PaginationInfo } from "react-migration/components/Pagination";
import { useRef, useState } from "react";

import { Loading } from "react-migration/components/Loading";
import { NoSearchResults } from "./components/NoSearchResults";
import { Table } from "react-migration/components/TableDeprecated";
import { TextInputV2 } from "react-migration/components/TextInputV2";
import clsx from "clsx";
import { router } from "src/js/router/react-router";
import { useDocumentColumns } from "./hooks/useDocumentColumns";
import { useEventListener } from "usehooks-ts";
import { usePagedTransactionsQuery } from "./hooks/usePagedTransactionsQuery";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";
import { TransactionPurchaseType } from "@gql/li-api/graphql";

const PER_PAGE = 15;

type DocumentsProps = {
  hideHeading?: boolean;
  pagePadding?: boolean;
};

export const Documents = ({ hideHeading = false, pagePadding = false }: DocumentsProps) => {
  const searchParams = new URLSearchParams(window.location.search);
  const pageNrQuery = searchParams.get("page");
  const [searchValue, setSearchValue] = useState(searchParams.get("search") || "");
  const [searchValueQuery, setSearchValueQuery] = useState(searchParams.get("search") || "");

  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState(() => {
    if (Number(pageNrQuery) - 1 > 0) {
      return Number(pageNrQuery) - 1;
    }
    return 0;
  });
  const { data, error, loading } = usePagedTransactionsQuery({
    page: currentPage,
    perPage: PER_PAGE,
    search: searchValueQuery,
    kind: [
      // @todo - Why are we not displaying Atkins and Letter Sending?
      TransactionPurchaseType.TitleRegisterDocument,
      TransactionPurchaseType.TitlePlanDocument,
      TransactionPurchaseType.OsMapPrint,
      TransactionPurchaseType.ThirdPartyOnDemand,
    ],
  });

  type queryParamsType = { page: string; search?: string };

  const pagination = data?.pagedTransactions.pagination;

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  };

  const documentRef = useRef(document);
  useEventListener(
    "keyup",
    (e) => {
      if (e.key === "Enter") {
        setSearchValueQuery(searchValue);
        const queryParams = { page: String(currentPage + 1) } as queryParamsType;
        if (searchValue) queryParams.search = searchValue;
        router.push({ query: queryParams });
      }
    },
    documentRef
  );

  const columns = useDocumentColumns();
  return (
    <div
      className={clsx("documents-tab flex flex-col flex-1 min-h-0 min-w-[1024px] w-full bg-background-surface", {
        "p-4": pagePadding,
      })}
    >
      {!hideHeading && (
        <h2 className="text-sm font-medium md:text-3xl mb-2 pb-[10px]">
          {t("dashboard.transactions.purchased_documents")}
        </h2>
      )}
      <div data-testid="documents-table" className="flex-1 min-h-0 overflow-auto w-full bg-background-surface">
        {loading && <Loading />}
        {error ? (
          <div>{t("dashboard.transactions.error")}</div>
        ) : (
          <>
            {((data?.pagedTransactions?.nodes?.length && !!pagination) || (searchValueQuery && !!pagination)) && (
              <PaginationContextProvider
                currentPage={pagination.currentPage}
                itemsPerPage={pagination.perPage}
                total={pagination.totalCount}
                pageCount={pagination.totalPages}
                onPageChange={handlePageChange}
              >
                <div className="flex items-end justify-between mb-4">
                  <PaginationInfo />
                  <TextInputV2
                    value={searchValue}
                    IconLeft={
                      <span className="flex w-4 text-neutral-400">
                        <SearchIcon />
                      </span>
                    }
                    placeholder={t("dashboard.transactions.search_for_document")}
                    onChange={handleSearch}
                  />
                </div>
                {data?.pagedTransactions?.nodes?.length > 0 && (
                  <div className={pagination.totalPages < 2 ? "pb-[40px]" : ""}>
                    <Table primaryKey="created" columns={columns} rows={data.pagedTransactions.nodes} />
                  </div>
                )}

                {pagination.totalPages > 1 && (
                  <div className="flex justify-center py-6">
                    <Pagination />
                  </div>
                )}
              </PaginationContextProvider>
            )}
          </>
        )}
        {searchValueQuery
          ? data?.pagedTransactions?.nodes?.length === 0 && <NoSearchResults />
          : data?.pagedTransactions?.nodes?.length === 0 && (
              <div>{t("dashboard.transactions.no_purchased_documents")}</div>
            )}
      </div>
    </div>
  );
};

const SearchIcon = () => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 16 16">
      <g clipPath="url(#clip0_3421_2488)">
        <path
          fill="currentColor"
          d="M12.021 11.078l2.856 2.854-.944.944-2.854-2.856a6.002 6.002 0 01-9.745-4.687c0-3.312 2.688-6 6-6a6.002 6.002 0 014.687 9.745zm-1.337-.495a4.665 4.665 0 00-3.35-7.917 4.665 4.665 0 00-4.667 4.667 4.665 4.665 0 007.917 3.35l.1-.1z"
        ></path>
      </g>
      <defs>
        <clipPath id="clip0_3421_2488">
          <path fill="#fff" d="M0 0H16V16H0z"></path>
        </clipPath>
      </defs>
    </svg>
  );
};
